import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback
} from 'react';
import { Tabs, Button } from '@superops/ui-design-system';
import { AccountClient } from '@nile/graphql';
import {
  CurrencyText,
  SuperList,
  useSuperListHook,
  ALL_TABLES
} from '@nile/components';
import { FormattedMessage } from 'react-intl';
import InvoiceContainer from '@nile/client_management/src/Pages/Protected/ClientNavigation/InvoiceContainer';
import { PaymentContainer } from '@nile/client_management/src/Pages/Protected/ClientNavigation/PaymentContainer.jsx';
import DateTimeCell from '@nile/mangifera/src/Components/Ticket/DateTimeCell';
import { useInvoiceStatementsStore } from '@nile/reports/src/modules/InvoiceStatements/invoiceStatementsStore';
import { InvoiceRedirectCell } from '@nile/client_management/src/Pages/Invoice/InvoiceTransactionListTable';
import { cloneDeep } from 'lodash';
import { useParams } from 'react-router-dom';
import { GET_PAID_INVOICE_LIST } from './invoiceGql';

const PAID_TABLE_NAME = 'invoiceStatementsPreviewPaid';

const getAttributeName = (col) => {
  return col?.accessorKey;
};

const TABS = [ALL_TABLES.invoice, PAID_TABLE_NAME, ALL_TABLES.paymentList];

function ResetButton({ activeIndex }) {
  const { id } = useParams();

  const { client: clientId } = useInvoiceStatementsStore();

  const { resetFilters, showResetFilter } = useSuperListHook({
    tableName: TABS[activeIndex],
    viewId: activeIndex === 0 ? id : TABS[activeIndex],
    showOnlyOnTableFilter: true
  });

  const resetFiltersRef = useRef(resetFilters);
  resetFiltersRef.current = resetFilters;

  useEffect(() => {
    if (clientId) {
      resetFiltersRef.current();
    }
  }, [clientId]);

  return (
    <div className="mlauto tw-pr-2">
      {showResetFilter && (
        <Button id="resetSuperListFilter" plain onClick={resetFilters}>
          <FormattedMessage id="ticket.details.resetFilters" />
        </Button>
      )}
    </div>
  );
}

function PaidTable() {
  const {
    client: clientId,
    reportAllowedDateRange
  } = useInvoiceStatementsStore();
  const [data, setData] = useState([]);

  const {
    tableCondition,
    advanceFilterCondition,
    updateTableFilterCondition
  } = useSuperListHook({
    tableName: PAID_TABLE_NAME,
    viewId: PAID_TABLE_NAME
  });

  const updateTableFilterConditionRef = useRef(updateTableFilterCondition);
  updateTableFilterConditionRef.current = updateTableFilterCondition;

  const updateConditions = useCallback((...args) => {
    return updateTableFilterConditionRef.current?.(...args);
  }, []);

  const baseAdvanceFilterCondition = useMemo(() => {
    const operands = [
      reportAllowedDateRange && {
        ...reportAllowedDateRange,
        attribute: 'invoiceDate'
      },
      clientId && {
        attribute: 'client.accountId',
        operator: 'is',
        value: clientId
      }
    ].filter(Boolean);

    return operands.length
      ? {
          joinOperator: 'AND',
          operands
        }
      : null;
  }, [reportAllowedDateRange, clientId]);

  const mergedAdvanceFilterCondition = useMemo(() => {
    if (baseAdvanceFilterCondition && advanceFilterCondition) {
      return {
        joinOperator: 'AND',
        operands: [baseAdvanceFilterCondition, advanceFilterCondition]
      };
    }
    return baseAdvanceFilterCondition || advanceFilterCondition || null;
  }, [baseAdvanceFilterCondition, advanceFilterCondition]);

  const cols = useMemo(() => {
    const c = [
      {
        Header: <FormattedMessage id="invoice.details.invoiceId" />,
        accessor: 'displayId',
        accessorKey: 'displayId',
        id: 'displayId',
        type: 'InvoiceRedirectCell',
        name: 'invoiceId',
        disableSortBy: false,
        disableFilters: false,
        filterType: 'text',
        dataType: 'string'
      },
      {
        Header: (
          <FormattedMessage id="invoice.partialPayments.paymentSummary.paymentDate" />
        ),
        accessor: 'paymentDate',
        accessorKey: 'paymentDate',
        id: 'paymentDate',
        type: 'DateTimeCell',
        dataType: 'date',
        name: 'paymentDate',
        disableSortBy: false,
        disableFilters: true
      },
      {
        Header: <FormattedMessage id="common.amount" />,
        accessor: 'totalAmount',
        accessorKey: 'totalAmount',
        id: 'totalAmount',
        type: 'Currency',
        name: 'totalAmount',
        disableSortBy: false,
        disableFilters: true
      }
    ];

    return c.map((item) => {
      return {
        ...item,
        field: item,
        helpers: {
          updateConditions,
          checkDynamicQuery: () => false,
          getDefaultValue: (f, condition) => {
            const operands = cloneDeep(condition.operands);
            const ruleIdx = operands.findIndex((el) => {
              return el.attribute === f.accessorKey;
            });
            if (ruleIdx > -1) {
              const rule = operands[ruleIdx];
              if (f.name !== 'statusEnum') {
                switch (f.filterType) {
                  case 'text':
                    return rule.value;
                  default:
                    return '';
                }
              }
            }
            return '';
          }
        }
      };
    });
  }, [updateConditions]);

  return (
    <div className="full-height">
      <SuperList
        tableName={PAID_TABLE_NAME}
        viewId={PAID_TABLE_NAME}
        columns={cols}
        defaultColumnCells={{
          DateTimeCell: DateTimeCell,
          Currency: ({ cell }: { cell: { value?: number | string } }) => (
            <CurrencyText value={Number(cell?.value ?? 0)} />
          ),
          InvoiceRedirectCell: ({ cell }: { cell: { value?: string } }) => (
            <InvoiceRedirectCell
              cell={{
                ...cell,
                row: {
                  original: {
                    invoice: { invoiceId: cell.row.original.invoiceId }
                  }
                }
              }}
            />
          )
        }}
        data={data}
        setData={setData}
        noBulkSelect
        idKey="invoiceId"
        tableCondition={tableCondition}
        client={AccountClient}
        query={GET_PAID_INVOICE_LIST}
        accessorKey="getInvoiceList"
        dataKey="invoices"
        defaultCondition={{
          joinOperator: 'AND',
          operands: [
            clientId && {
              attribute: 'client.accountId',
              operator: 'is',
              value: clientId
            },
            {
              attribute: 'statusEnum',
              operator: 'is',
              value: 'PAID'
            }
          ].filter(Boolean)
        }}
        defaultSort={[
          {
            attribute: 'paymentDate',
            order: 'DESC'
          }
        ]}
        advanceFilterCondition={mergedAdvanceFilterCondition}
        getAttributeName={getAttributeName}
      />
    </div>
  );
}

export function StatementPreviewTable(): JSX.Element {
  const {
    client: clientId,
    reportAllowedDateRange,
    previewTabIndex,
    setPreviewTabIndex
  } = useInvoiceStatementsStore();

  const adjustedAdvanceFilterCondition = useMemo(() => {
    return [
      reportAllowedDateRange,
      clientId && {
        attribute: 'client.accountId',
        operator: 'is',
        value: clientId
      }
    ].filter(Boolean);
  }, [reportAllowedDateRange, clientId]);

  const tabs = useMemo(
    () => [
      {
        id: 'unpaid',
        content: (
          <span>
            <FormattedMessage id="invoice.statements.unpaidInvoices" />
          </span>
        )
      },
      {
        id: 'paid',
        content: (
          <span>
            <FormattedMessage id="invoice.statements.paidInvoices" />
          </span>
        )
      },
      {
        id: 'payments',
        content: (
          <span>
            <FormattedMessage id="invoice.statements.payments" />
          </span>
        )
      }
    ],
    []
  );

  const paymentConditions = useMemo(() => {
    return [
      reportAllowedDateRange
        ? { ...reportAllowedDateRange, attribute: 'paymentDate' }
        : undefined,
      clientId && {
        attribute: 'client.accountId',
        operator: 'is',
        value: clientId
      }
    ].filter(Boolean);
  }, [reportAllowedDateRange, clientId]);

  function renderPanel(): JSX.Element {
    if (previewTabIndex === 0) {
      return (
        <div className="full-height">
          <InvoiceContainer
            hideColumns={['client.name']}
            hideActions
            appendViewIdToListInfo={false}
            advanceFilterCondition={adjustedAdvanceFilterCondition}
            defaultCondition={{
              joinOperator: 'AND',
              operands: [
                {
                  attribute: 'statusEnum',
                  operator: 'notIncludes',
                  value: ['VOID', 'PAID', 'DRAFT']
                },
                {
                  attribute: 'client.accountId',
                  operator: 'is',
                  value: clientId
                }
              ].filter(Boolean)
            }}
          />
        </div>
      );
    }
    if (previewTabIndex === 1) return <PaidTable />;
    return (
      <div className="full-height">
        <PaymentContainer
          hideColumns={['client.name']}
          clientId={clientId}
          hideActions
          conditions={paymentConditions}
        />
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-mt-4">
      <ResetButton activeIndex={previewTabIndex} />
      <Tabs
        tabs={tabs}
        selected={previewTabIndex}
        onSelect={setPreviewTabIndex}
        fillWhiteBg
        addBottomShadow
        adaptheight
        panelHeight="50px"
      >
        <div className="full-height tw-flex-1 tw-overflow-auto">
          {renderPanel()}
        </div>
      </Tabs>
    </div>
  );
}
